import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import { BANNER_HEIGHT, TOP_NAVIGATION_HEIGHT } from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import { CONVERSATION_ASSISTANT_CONTAINER_WIDTH } from '../../constants/public';
const rootStyles = xcss({
  right: 'space.0',
  display: 'flex',
  position: 'fixed',
  height: `calc(100vh - ${TOP_NAVIGATION_HEIGHT} - ${BANNER_HEIGHT})`
});
const handleStyles = xcss({
  cursor: 'ew-resize',
  userSelect: 'none',
  position: 'absolute',
  top: 'space.0',
  bottom: 'space.0',
  left: 'space.negative.300',
  width: '24px',
  transition: 'border 0.2s ease-in-out',
  borderRight: '2px solid transparent',
  ':hover': {
    borderRight: `2px solid ${"var(--ds-background-information-bold-hovered, #0055CC)"}`
  }
});
export const ResizeWrapper = ({
  children,
  onResizeComplete,
  initialWidth = CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
  minWidth = CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
  maxWidth = '50vw'
}) => {
  const [width, setWidth] = useState(initialWidth);
  const wrapperRef = useRef(null);
  const isDraggingRef = useRef(false);
  const initialMousePositionRef = useRef(0);
  const prevDistanceMovedRef = useRef(0);
  const rafRef = useRef(null);
  const getWrapperWidth = () => {
    var _wrapperRef$current, _wrapperRef$current$g;
    return ((_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : (_wrapperRef$current$g = _wrapperRef$current.getBoundingClientRect()) === null || _wrapperRef$current$g === void 0 ? void 0 : _wrapperRef$current$g.width) || 0;
  };
  const handleMouseMove = useCallback(event => {
    if (!isDraggingRef.current) {
      return;
    }
    const distanceMoved = event.clientX - initialMousePositionRef.current;
    let updatedWidth;
    const currentWidth = getWrapperWidth();

    // Ignore events without change
    if (distanceMoved === prevDistanceMovedRef.current || distanceMoved === 0) {
      return;
    }

    // Acknowledge latest direction adjustment
    prevDistanceMovedRef.current = distanceMoved;

    // Clear queued animation frame
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      // Update widths with upper and lower limits respected when `minWidth` and `maxWidth` are `number`
      // When `string`, we'll respect this value on the styles of the wrapper for use-cases such as '100%', '70vw' etc.
      if (distanceMoved < 0) {
        updatedWidth = currentWidth + Math.abs(distanceMoved);
        if (typeof maxWidth === 'number' && !isNaN(maxWidth)) {
          updatedWidth = Math.min(updatedWidth, maxWidth);
        }
      } else {
        updatedWidth = currentWidth - distanceMoved;
        if (typeof minWidth === 'number' && !isNaN(minWidth)) {
          updatedWidth = Math.max(updatedWidth, minWidth);
        }
      }
      setWidth(updatedWidth);
      initialMousePositionRef.current = event.clientX;
    });
  }, [maxWidth, minWidth]);
  const handleMouseDown = useCallback(event => {
    // Indicate dragging
    isDraggingRef.current = true;
    // Indicate starting point
    initialMousePositionRef.current = event.clientX;
  }, []);
  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current && onResizeComplete) {
      var _wrapperRef$current2;
      onResizeComplete(((_wrapperRef$current2 = wrapperRef.current) === null || _wrapperRef$current2 === void 0 ? void 0 : _wrapperRef$current2.getBoundingClientRect().width) || width);
    }
    isDraggingRef.current = false;
  }, [onResizeComplete, width]);
  useEffect(() => {
    const mouseMoveHandler = bind(window, {
      type: 'mousemove',
      listener: handleMouseMove
    });
    const mouseUpHandler = bind(window, {
      type: 'mouseup',
      listener: handleMouseUp
    });
    return () => {
      mouseMoveHandler();
      mouseUpHandler();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleMouseMove, handleMouseUp]);
  return /*#__PURE__*/React.createElement(Box, {
    xcss: rootStyles,
    ref: wrapperRef,
    role: "presentation",
    testId: "panel-resize-wrapper",
    style: {
      width,
      maxWidth,
      minWidth
    }
  }, /*#__PURE__*/React.createElement(Box, {
    testId: "panel-resize-handle",
    onMouseDown: handleMouseDown,
    xcss: handleStyles
  }), children);
};